import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Lock,
  Security,
} from '@mui/icons-material';
import { Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import EntityView from 'components/entityView';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { FinsightEntity } from 'shared';

type EncryptionContextType = {
  addEntityEncryptionKey: (entity: FinsightEntity, key: string) => void;
  entityEncryptionKeys: Record<
    string,
    {
      key: string;
      entity: FinsightEntity;
    }
  >;
};

const EncryptionContext = createContext<EncryptionContextType>({
  entityEncryptionKeys: {},
  addEntityEncryptionKey: () => {},
});

export function EncryptionProvider(props: React.PropsWithChildren) {
  const [entityEncryptionKeys, setEntityEncryptionKeys] = useState<
    EncryptionContextType['entityEncryptionKeys']
  >({});

  const addEntityEncryptionKey = (entity: FinsightEntity, key: string) => {
    setEntityEncryptionKeys((prev) => ({
      ...prev,
      [entity.id]: { key, entity },
    }));
  };

  const encryptionKeys = useMemo(() => {
    return Object.values(entityEncryptionKeys);
  }, [entityEncryptionKeys]);

  return (
    <EncryptionContext.Provider
      value={{ entityEncryptionKeys, addEntityEncryptionKey }}
    >
      {props.children}
      {!!encryptionKeys.length && (
        <EncryptionKeyWidget
          encryptionKeys={encryptionKeys}
          removeEntityEncryptionKey={(entityId) => {
            setEntityEncryptionKeys((prev) => {
              const newEntityEncryptionKeys = { ...prev };
              delete newEntityEncryptionKeys[entityId];
              return newEntityEncryptionKeys;
            });
          }}
        />
      )}
    </EncryptionContext.Provider>
  );
}

function EncryptionKeyWidget(props: {
  encryptionKeys: EncryptionContextType['entityEncryptionKeys'][string][];
  removeEntityEncryptionKey: (entityId: string) => void;
}) {
  const { encryptionKeys, removeEntityEncryptionKey } = props;
  const [open, setOpen] = useState(false);

  return (
    <Paper
      elevation={4}
      sx={{
        p: 2,
        right: 24,
        bottom: 24,
        position: 'fixed',
        minWidth: open ? 600 : 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Security color="warning" />
          <Typography variant="h6">
            {encryptionKeys.length} Entities Are Decrypted
          </Typography>
        </Stack>
        <IconButton onClick={() => setOpen(!open)}>
          {open ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        </IconButton>
      </Stack>
      {open && (
        <>
          <Divider
            sx={{
              mt: 1,
              mb: 2,
            }}
          />
          {encryptionKeys.map(({ entity }) => (
            <Stack
              key={entity.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                '&>:first-child': {
                  flex: 1,
                },
              }}
            >
              <EntityView entity={entity} viewType="list" disableActions />
              <IconButton
                onClick={() => {
                  removeEntityEncryptionKey(entity.id);
                }}
              >
                <Lock color="error" />
              </IconButton>
            </Stack>
          ))}
        </>
      )}
    </Paper>
  );
}

export function useEntityEncryption(entityId: string) {
  const context = useContext(EncryptionContext);

  const encryptionKey = context.entityEncryptionKeys[entityId]?.key;

  return {
    encryptionKey,
    addEntityEncryptionKey: (key: string, entity: FinsightEntity) => {
      context.addEntityEncryptionKey(entity, key);
    },
  };
}
